import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

const customerWarehousesAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

customerWarehousesAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return applyWarehouseFilter(config);
  },
  (error) => Promise.reject(error)
);

customerWarehousesAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

class CustomerWarehousesService {
  /** List all warehouses for a customer */
  async getWarehouses(customerId, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.skip !== undefined) queryParams.append('skip', params.skip);
      if (params.limit !== undefined) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);

      const query = queryParams.toString();
      const response = await customerWarehousesAPI.get(
        `/customers/${customerId}/warehouses${query ? `?${query}` : ''}`
      );
      const data = response.data;
      return {
        success: true,
        data: data?.warehouses || data?.items || data || [],
        pagination: data?.pagination || {},
        total: data?.total || data?.count || (Array.isArray(data) ? data.length : 0),
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
        data: [],
      };
    }
  }

  /** Create a warehouse for a customer */
  async createWarehouse(customerId, payload) {
    try {
      const response = await customerWarehousesAPI.post(
        `/customers/${customerId}/warehouses`,
        payload
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  }

  /** Get a single warehouse by id */
  async getWarehouseById(customerId, warehouseId) {
    try {
      const response = await customerWarehousesAPI.get(
        `/customers/${customerId}/warehouses/${warehouseId}`
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  }

  /** Update a warehouse */
  async updateWarehouse(customerId, warehouseId, payload) {
    try {
      const response = await customerWarehousesAPI.put(
        `/customers/${customerId}/warehouses/${warehouseId}`,
        payload
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  }

  /** Delete a warehouse */
  async deleteWarehouse(customerId, warehouseId) {
    try {
      await customerWarehousesAPI.delete(
        `/customers/${customerId}/warehouses/${warehouseId}`
      );
      return { success: true };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  }

  /** Set a warehouse as the default for a customer */
  async setDefaultWarehouse(customerId, warehouseId) {
    try {
      const response = await customerWarehousesAPI.patch(
        `/customers/${customerId}/warehouses/${warehouseId}/set-default`
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: extractApiError(error) };
    }
  }
}

const customerWarehousesService = new CustomerWarehousesService();
export default customerWarehousesService;
